/**
 * textComparer.ts
 *
 * Slide matching and text diffing utilities for the "Compare Text" feature.
 * Takes slide data extracted from two PPTX files and produces a structured
 * comparison report.
 *
 * No external dependencies — LCS and Jaccard are implemented from scratch.
 */

import type { ExtractedShape, ExtractedSlide } from "./pptxTextExtractor";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type DiffType = "equal" | "delete" | "insert" | "moved";

export interface DiffHunk {
  type: DiffType;
  text: string;
  fromSlide?: number;
  toSlide?: number;
}

export interface CellDiff {
  row: number;
  col: number;
  origText: string;
  modText: string;
  diffs: DiffHunk[];
}

export type ShapeStatus = "unchanged" | "modified" | "deleted" | "added";

export interface ShapeResult {
  origShape: ExtractedShape | null;
  modShape: ExtractedShape | null;
  status: ShapeStatus;
  diffs: DiffHunk[];
  isTableDiff?: boolean;
  cellDiffs?: CellDiff[];
}

export type SlideStatus = "unchanged" | "modified" | "removed" | "added";

export interface SlideComparison {
  origSlideIndex: number | null;
  modSlideIndex: number | null;
  origSlideNumber: number | null;
  modSlideNumber: number | null;
  similarity: number;
  status: SlideStatus;
  shapeResults: ShapeResult[];
}

export interface ComparisonSummary {
  totalOrig: number;
  totalMod: number;
  matched: number;
  added: number;
  removed: number;
  unchanged: number;
  modified: number;
}

export interface ComparisonReport {
  summary: ComparisonSummary;
  slideComparisons: SlideComparison[];
}

interface SlideMatch {
  origIdx: number;
  modIdx: number;
  similarity: number;
}

interface MatchResult {
  matched: SlideMatch[];
  unmatchedOrig: number[];
  unmatchedMod: number[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Tokenize a string into lowercase words (split on whitespace).
 * Returns an empty array for falsy / whitespace-only input.
 */
function tokenize(text: string): string[] {
  if (!text || typeof text !== "string") return [];
  const trimmed = text.trim();
  if (trimmed.length === 0) return [];
  return trimmed.split(/\s+/);
}

/**
 * Normalize text for comparison: collapse whitespace, trim, normalize unicode.
 * This prevents false positives from invisible formatting differences.
 */
function normalizeText(text: string): string {
  if (!text) return "";
  return text
    .replace(/[\r\n]+/g, "\n")        // normalize line endings
    .replace(/[\t ]+/g, " ")          // collapse horizontal whitespace
    .replace(/\u00A0/g, " ")          // non-breaking space -> regular space
    .replace(/[\u200B-\u200D\uFEFF]/g, "")  // zero-width chars
    .trim();
}

/**
 * Build the set of word bigrams from a token array.
 * A bigram is two consecutive tokens joined by a space.
 * Single-token input returns a set containing that token.
 */
function buildBigrams(tokens: string[]): Set<string> {
  const set = new Set<string>();
  if (tokens.length === 0) return set;
  if (tokens.length === 1) {
    set.add(tokens[0]!);
    return set;
  }
  for (let i = 0; i < tokens.length - 1; i++) {
    set.add(tokens[i]! + " " + tokens[i + 1]!);
  }
  return set;
}

/**
 * Jaccard similarity between two sets.
 * Returns 0 when both sets are empty, 1 when identical.
 */
function jaccardSimilarity(setA: Set<string>, setB: Set<string>): number {
  if (setA.size === 0 && setB.size === 0) return 1;
  if (setA.size === 0 || setB.size === 0) return 0;
  let intersection = 0;
  for (const item of setA) {
    if (setB.has(item)) intersection++;
  }
  const union = setA.size + setB.size - intersection;
  return union === 0 ? 0 : intersection / union;
}

/**
 * Levenshtein edit distance between two strings.
 * Uses O(min(n,m)) space with a single-row DP approach.
 * Caps at maxLen characters for performance.
 */
function levenshteinDistance(a: string, b: string, maxLen = 500): number {
  if (a === b) return 0;
  let s = a.length > maxLen ? a.substring(0, maxLen) : a;
  let t = b.length > maxLen ? b.substring(0, maxLen) : b;

  // Ensure s is the shorter string for O(min) space
  if (s.length > t.length) { const tmp = s; s = t; t = tmp; }
  const sLen = s.length;
  const tLen = t.length;
  if (sLen === 0) return tLen;

  const prev = new Uint16Array(sLen + 1);
  for (let i = 0; i <= sLen; i++) prev[i] = i;

  for (let j = 1; j <= tLen; j++) {
    let prevDiag = prev[0]!;
    prev[0] = j;
    for (let i = 1; i <= sLen; i++) {
      const temp = prev[i]!;
      if (s[i - 1] === t[j - 1]) {
        prev[i] = prevDiag;
      } else {
        prev[i] = 1 + Math.min(prevDiag, prev[i]!, prev[i - 1]!);
      }
      prevDiag = temp;
    }
  }
  return prev[sLen]!;
}

/**
 * Normalized Levenshtein similarity (0–1).
 * Compares character-by-character, catching single-char differences like
 * apostrophes, extra spaces, slashes, etc. that word-bigram Jaccard misses.
 */
function levenshteinSimilarity(a: string, b: string): number {
  if (!a && !b) return 1;
  if (!a || !b) return 0;
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return 1;
  const dist = levenshteinDistance(a, b);
  return 1 - dist / maxLen;
}

/**
 * Compute the LCS (Longest Common Subsequence) table for two token arrays.
 * Returns the DP table (dimensions [n+1][m+1]).
 */
function lcsTable(a: string[], b: string[]): { dp: Uint16Array; cols: number } {
  const n = a.length;
  const m = b.length;
  // Use flat arrays for performance; dp[i][j] => dp[i * (m+1) + j]
  const cols = m + 1;
  const dp = new Uint16Array((n + 1) * cols);
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i * cols + j] = dp[(i - 1) * cols + (j - 1)]! + 1;
      } else {
        dp[i * cols + j] = Math.max(
          dp[(i - 1) * cols + j]!,
          dp[i * cols + (j - 1)]!
        );
      }
    }
  }
  return { dp, cols };
}

/**
 * Back-track the LCS table to recover the actual subsequence indices.
 * Returns an array of { aIdx, bIdx } pairs.
 */
function lcsBacktrack(a: string[], b: string[], dp: Uint16Array, cols: number): { aIdx: number; bIdx: number }[] {
  const pairs: { aIdx: number; bIdx: number }[] = [];
  let i = a.length;
  let j = b.length;
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) {
      pairs.push({ aIdx: i - 1, bIdx: j - 1 });
      i--;
      j--;
    } else if (dp[(i - 1) * cols + j]! >= dp[i * cols + (j - 1)]!) {
      i--;
    } else {
      j--;
    }
  }
  pairs.reverse();
  return pairs;
}

/**
 * Simple string hash (djb2) for move detection.
 */
function hashString(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    // djb2: hash * 33 + c. Math.imul keeps the running value in 32-bit range
    // (wraparound, not truncation) — this is a deterministic bucket key only.
    hash = Math.imul(hash, 33) + (str.codePointAt(i) ?? 0);
  }
  return hash;
}

// ---------------------------------------------------------------------------
// 1. matchSlides
// ---------------------------------------------------------------------------

// A slide pair may be treated as the same (edited) slide when their word-bigram
// Jaccard clears JACCARD_MATCH, OR — for short text like titles, where a couple
// of edits can wipe out every shared bigram — when their character Levenshtein
// clears LEV_MATCH. Both are calibrated to sit above genuinely unrelated slides
// (which score jaccard 0 / lev ≲ 0.54) and below real edits.
const JACCARD_MATCH = 0.15;
const LEV_MATCH = 0.6;
const SHORT_TEXT_LEN = 200;

// Alignment moves recorded in the back-pointer matrix.
const MOVE_DIAG = 1; // pair orig[i] with mod[j]
const MOVE_ORIG_GAP = 2; // orig[i] unpaired (removed)
const MOVE_MOD_GAP = 3; // mod[j] unpaired (added)
const GAP_COST = -0.05; // small cost per unpaired slide; any allowed match outweighs it

/** Highest-scoring alignment move for one DP cell. */
function _bestMove(diag: number, up: number, left: number): { score: number; move: number } {
  if (diag >= up && diag >= left) return { score: diag, move: MOVE_DIAG };
  if (up >= left) return { score: up, move: MOVE_ORIG_GAP };
  return { score: left, move: MOVE_MOD_GAP };
}

/** Walk the back-pointer matrix to recover the matched (origPos, modPos) pairs. */
function _backtrack(bk: number[][], n: number, m: number): Array<[number, number]> {
  const pairs: Array<[number, number]> = [];
  let i = n;
  let j = m;
  while (i > 0 || j > 0) {
    const move = bk[i]![j]!;
    if (i > 0 && j > 0 && move === MOVE_DIAG) {
      pairs.push([i - 1, j - 1]);
      i--;
      j--;
    } else if (i > 0 && move === MOVE_ORIG_GAP) {
      i--;
    } else {
      j--;
    }
  }
  pairs.reverse();
  return pairs;
}

/**
 * Order-preserving (Needleman–Wunsch) alignment of two slide sequences. Returns
 * the matched (origPos, modPos) index pairs in order; unpaired positions are the
 * caller's removed / added slides. Because alignment respects sequence order it
 * cannot cross-match, and only `allowed` pairs may align — so two slides with no
 * real overlap stay unpaired instead of being force-matched by position.
 */
function _alignByOrder(sim: number[][], allowed: boolean[][], n: number, m: number): Array<[number, number]> {
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  const bk: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = 1; i <= n; i++) {
    dp[i]![0] = i * GAP_COST;
    bk[i]![0] = MOVE_ORIG_GAP;
  }
  for (let j = 1; j <= m; j++) {
    dp[0]![j] = j * GAP_COST;
    bk[0]![j] = MOVE_MOD_GAP;
  }
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      const diag = allowed[i - 1]![j - 1]! ? dp[i - 1]![j - 1]! + sim[i - 1]![j - 1]! : -Infinity;
      const { score, move } = _bestMove(diag, dp[i - 1]![j]! + GAP_COST, dp[i]![j - 1]! + GAP_COST);
      dp[i]![j] = score;
      bk[i]![j] = move;
    }
  }
  return _backtrack(bk, n, m);
}

/**
 * Match slides between two decks by word-bigram (and short-text Levenshtein)
 * similarity, aligned in sequence order so an edited slide stays paired with its
 * original instead of being reported as a removal plus an addition.
 */
export function matchSlides(originalSlides: ExtractedSlide[], modifiedSlides: ExtractedSlide[]): MatchResult {
  if (!originalSlides || originalSlides.length === 0) {
    return {
      matched: [],
      unmatchedOrig: [],
      unmatchedMod: modifiedSlides
        ? modifiedSlides.map((s) => s.slideIndex)
        : [],
    };
  }
  if (!modifiedSlides || modifiedSlides.length === 0) {
    return {
      matched: [],
      unmatchedOrig: originalSlides.map((s) => s.slideIndex),
      unmatchedMod: [],
    };
  }

  const n = originalSlides.length;
  const m = modifiedSlides.length;

  // Pre-compute bigram sets + normalized full text for each slide.
  const origBigrams = originalSlides.map((s) => buildBigrams(tokenize((s.fullText || "").toLowerCase())));
  const modBigrams = modifiedSlides.map((s) => buildBigrams(tokenize((s.fullText || "").toLowerCase())));
  const origText = originalSlides.map((s) => normalizeText(s.fullText || "").toLowerCase());
  const modText = modifiedSlides.map((s) => normalizeText(s.fullText || "").toLowerCase());

  // Similarity + eligibility matrices.
  const sim: number[][] = [];
  const allowed: boolean[][] = [];
  for (let i = 0; i < n; i++) {
    sim[i] = [];
    allowed[i] = [];
    for (let j = 0; j < m; j++) {
      const jac = jaccardSimilarity(origBigrams[i]!, modBigrams[j]!);
      // Levenshtein only rescues short text and only when bigrams already fell
      // short — avoids O(len²) work on long slides and keeps it off the hot path.
      const short = origText[i]!.length <= SHORT_TEXT_LEN && modText[j]!.length <= SHORT_TEXT_LEN;
      const lev = jac < JACCARD_MATCH && short ? levenshteinSimilarity(origText[i]!, modText[j]!) : 0;
      sim[i]![j] = Math.max(jac, lev);
      allowed[i]![j] = jac >= JACCARD_MATCH || lev >= LEV_MATCH;
    }
  }

  const usedOrig = new Set<number>();
  const usedMod = new Set<number>();
  const matched: SlideMatch[] = _alignByOrder(sim, allowed, n, m).map(([i, j]) => {
    usedOrig.add(i);
    usedMod.add(j);
    return {
      origIdx: originalSlides[i]!.slideIndex,
      modIdx: modifiedSlides[j]!.slideIndex,
      similarity: Math.round(sim[i]![j]! * 1000) / 1000,
    };
  });

  const unmatchedOrig = originalSlides.filter((_, i) => !usedOrig.has(i)).map((s) => s.slideIndex);
  const unmatchedMod = modifiedSlides.filter((_, j) => !usedMod.has(j)).map((s) => s.slideIndex);

  return { matched, unmatchedOrig, unmatchedMod };
}

// ---------------------------------------------------------------------------
// 2. diffTexts
// ---------------------------------------------------------------------------

/** Build raw diff hunks by walking the LCS-matched index pairs. */
function _buildHunks(a: string[], b: string[], pairs: { aIdx: number; bIdx: number }[]): DiffHunk[] {
  const hunks: DiffHunk[] = [];
  let ai = 0;
  let bi = 0;
  for (const { aIdx, bIdx } of pairs) {
    if (ai < aIdx) hunks.push({ type: "delete", text: a.slice(ai, aIdx).join(" ") });
    if (bi < bIdx) hunks.push({ type: "insert", text: b.slice(bi, bIdx).join(" ") });
    hunks.push({ type: "equal", text: a[aIdx]! });
    ai = aIdx + 1;
    bi = bIdx + 1;
  }
  if (ai < a.length) hunks.push({ type: "delete", text: a.slice(ai).join(" ") });
  if (bi < b.length) hunks.push({ type: "insert", text: b.slice(bi).join(" ") });
  return hunks;
}

/** Coalesce runs of same-type hunks into one hunk each. */
function _mergeAdjacentHunks(hunks: DiffHunk[]): DiffHunk[] {
  const merged: DiffHunk[] = [];
  for (const h of hunks) {
    const prev = merged[merged.length - 1];
    if (prev?.type === h.type) prev.text += " " + h.text;
    else merged.push(h);
  }
  return merged;
}

/**
 * Word-level diff between two text strings using LCS.
 */
export function diffTexts(originalText: string, modifiedText: string): DiffHunk[] {
  const a = tokenize(originalText);
  const b = tokenize(modifiedText);

  if (a.length === 0 && b.length === 0) return [];
  if (a.length === 0) return [{ type: "insert", text: b.join(" ") }];
  if (b.length === 0) return [{ type: "delete", text: a.join(" ") }];

  const { dp, cols } = lcsTable(a, b);
  const merged = _mergeAdjacentHunks(_buildHunks(a, b, lcsBacktrack(a, b, dp, cols)));

  // Whitespace-only change detection: if the word diff is all-equal but the raw
  // texts differ (extra/leading/trailing spaces), flag as a full replacement.
  if (merged.every((h) => h.type === "equal") && originalText !== modifiedText) {
    return [
      { type: "delete", text: originalText },
      { type: "insert", text: modifiedText },
    ];
  }

  return merged;
}

// ---------------------------------------------------------------------------
// 3. diffSlideShapes
// ---------------------------------------------------------------------------

/**
 * Shape-level comparison for a matched slide pair.
 *
 * MATCHING STRATEGY: Text-similarity-first, NOT name-first.
 * Shape names in PowerPoint are arbitrary ("TextBox 1", "Rectangle 5") and
 * often don't match between files even when the content is the same.
 * Instead, we build a full similarity matrix of ALL shapes (original × modified)
 * using text content and use greedy best-match assignment.
 * Shape name is only used as a small bonus tiebreaker, never as the primary match.
 */
/**
 * Similarity (0–1) between two shapes. Empty text on either side → 0; exact
 * (normalized) text → 1; otherwise max(Jaccard bigrams, Levenshtein) with a
 * small +0.05 bonus when the shape names match (tiebreaker only).
 */
function _shapeSimilarity(
  orig: ExtractedShape,
  mod: ExtractedShape,
  origBigram: Set<string>,
  modBigram: Set<string>,
): number {
  const origText = normalizeText(orig.text || "");
  const modText = normalizeText(mod.text || "");
  if (!origText || !modText) return 0;
  if (origText === modText) return 1.0;

  const sim = Math.max(
    jaccardSimilarity(origBigram, modBigram),
    levenshteinSimilarity(origText.toLowerCase(), modText.toLowerCase()),
  );
  const sameName =
    !!orig.name && !!mod.name && orig.name.toLowerCase() === mod.name.toLowerCase();
  return Math.min(1.0, sim + (sameName ? 0.05 : 0));
}

/** Every (origIdx, modIdx) pair scoring at least the 0.15 match threshold. */
function _shapeCandidates(
  origArr: ExtractedShape[],
  modArr: ExtractedShape[],
  origBigrams: Set<string>[],
  modBigrams: Set<string>[],
): { i: number; j: number; sim: number }[] {
  const candidates: { i: number; j: number; sim: number }[] = [];
  for (let i = 0; i < origArr.length; i++) {
    for (let j = 0; j < modArr.length; j++) {
      const sim = _shapeSimilarity(origArr[i]!, modArr[j]!, origBigrams[i]!, modBigrams[j]!);
      if (sim >= 0.15) candidates.push({ i, j, sim });
    }
  }
  return candidates;
}

/** Diff one matched pair — cell-level for tables, RAW word-level otherwise. */
function _diffMatchedPair(origShape: ExtractedShape, modShape: ExtractedShape): ShapeResult {
  const origGrid = origShape.cellGrid;
  const modGrid = modShape.cellGrid;
  if (origShape.isTable && modShape.isTable && origGrid && modGrid) {
    const cellDiffs = _diffTableCells(origGrid, modGrid);
    return {
      origShape, modShape,
      status: cellDiffs.length > 0 ? "modified" : "unchanged",
      diffs: [], isTableDiff: true, cellDiffs,
    };
  }
  // RAW text (not normalized) so whitespace-only edits still register.
  const origTextRaw = origShape.text || "";
  const modTextRaw = modShape.text || "";
  if (origTextRaw === modTextRaw) return { origShape, modShape, status: "unchanged", diffs: [] };
  return { origShape, modShape, status: "modified", diffs: diffTexts(origTextRaw, modTextRaw) };
}

/** Shapes with meaningful text not claimed by a match → deleted (orig) / added (mod). */
function _unmatchedShapeResults(
  arr: ExtractedShape[],
  used: Set<number>,
  kind: "deleted" | "added",
): ShapeResult[] {
  const out: ShapeResult[] = [];
  for (let i = 0; i < arr.length; i++) {
    if (used.has(i)) continue;
    const shape = arr[i]!;
    const text = normalizeText(shape.text || "");
    if (!text) continue;
    out.push(
      kind === "deleted"
        ? { origShape: shape, modShape: null, status: "deleted", diffs: [{ type: "delete", text }] }
        : { origShape: null, modShape: shape, status: "added", diffs: [{ type: "insert", text }] },
    );
  }
  return out;
}

export function diffSlideShapes(originalShapes: ExtractedShape[], modifiedShapes: ExtractedShape[]): ShapeResult[] {
  const origArr = originalShapes || [];
  const modArr = modifiedShapes || [];

  // Pre-compute bigrams for all shapes.
  const origBigrams = origArr.map((s) => buildBigrams(tokenize(normalizeText(s.text || "").toLowerCase())));
  const modBigrams = modArr.map((s) => buildBigrams(tokenize(normalizeText(s.text || "").toLowerCase())));

  // Greedy best-match assignment: highest similarity first.
  const candidates = _shapeCandidates(origArr, modArr, origBigrams, modBigrams);
  candidates.sort((a, b) => b.sim - a.sim);

  const results: ShapeResult[] = [];
  const usedOrig = new Set<number>();
  const usedMod = new Set<number>();
  for (const c of candidates) {
    if (usedOrig.has(c.i) || usedMod.has(c.j)) continue;
    usedOrig.add(c.i);
    usedMod.add(c.j);
    results.push(_diffMatchedPair(origArr[c.i]!, modArr[c.j]!));
  }

  results.push(
    ..._unmatchedShapeResults(origArr, usedOrig, "deleted"),
    ..._unmatchedShapeResults(modArr, usedMod, "added"),
  );
  return results;
}

// ---------------------------------------------------------------------------
// 3b. _diffTableCells  (cell-level table comparison)
// ---------------------------------------------------------------------------

/**
 * Compare two cellGrid arrays cell-by-cell.
 * Returns an array of changed cells: { row, col, origText, modText, diffs }
 */
function _diffTableCells(origGrid: string[][], modGrid: string[][]): CellDiff[] {
  const changes: CellDiff[] = [];
  const maxRows = Math.max(origGrid.length, modGrid.length);

  for (let r = 0; r < maxRows; r++) {
    const origRow = origGrid[r] || [];
    const modRow = modGrid[r] || [];
    const maxCols = Math.max(origRow.length, modRow.length);

    for (let c = 0; c < maxCols; c++) {
      const origText = origRow[c] ?? "";
      const modText = modRow[c] ?? "";

      if (origText === modText) continue;

      // Produce word-level diff for display
      const diffs = diffTexts(origText, modText);
      changes.push({ row: r, col: c, origText, modText, diffs });
    }
  }
  return changes;
}

// ---------------------------------------------------------------------------
// 4. detectMoves
// ---------------------------------------------------------------------------

interface MoveEntry {
  slideIdx: number;
  shapeIdx: number;
  diffIdx: number;
  words: string[];
}

/**
 * Post-processing: scans all slide diffs to identify text deleted from one
 * slide that appears as inserted on another. Marks those as "moved".
 *
 * Mutates the input array in place and returns it.
 */
/** Index one shape's delete/insert hunks (≥ MIN_WORDS) into the hash buckets. */
function _indexShapeDiffs(
  diffs: DiffHunk[],
  slideIdx: number,
  shapeIdx: number,
  buckets: { deletions: Map<number, MoveEntry[]>; insertions: Map<number, MoveEntry[]> },
): void {
  const MIN_WORDS = 3;
  for (let diffIdx = 0; diffIdx < diffs.length; diffIdx++) {
    const d = diffs[diffIdx]!;
    if (d.type !== "delete" && d.type !== "insert") continue;
    const words = tokenize(d.text);
    if (words.length < MIN_WORDS) continue;
    const key = hashString(words.join(" ").toLowerCase());
    const bucket = d.type === "delete" ? buckets.deletions : buckets.insertions;
    if (!bucket.has(key)) bucket.set(key, []);
    bucket.get(key)!.push({ slideIdx, shapeIdx, diffIdx, words });
  }
}

/** Bucket every delete/insert hunk across all slides by its lowercased text hash. */
function _indexHunks(slideResults: SlideComparison[]): {
  deletions: Map<number, MoveEntry[]>;
  insertions: Map<number, MoveEntry[]>;
} {
  const buckets = { deletions: new Map<number, MoveEntry[]>(), insertions: new Map<number, MoveEntry[]>() };
  for (let si = 0; si < slideResults.length; si++) {
    const shapeResults = slideResults[si]!.shapeResults || [];
    for (let shi = 0; shi < shapeResults.length; shi++) {
      _indexShapeDiffs(shapeResults[shi]!.diffs || [], si, shi, buckets);
    }
  }
  return buckets;
}

/** Stamp a hunk as "moved" with its from/to slide indices. */
function _markMoved(slideResults: SlideComparison[], e: MoveEntry, fromSlide: number, toSlide: number): void {
  const diff = slideResults[e.slideIdx]!.shapeResults[e.shapeIdx]!.diffs[e.diffIdx]!;
  diff.type = "moved";
  diff.fromSlide = fromSlide;
  diff.toSlide = toSlide;
}

/** Pair each deletion with the first same-text insertion on a different slide → "moved". */
function _matchMoves(
  slideResults: SlideComparison[],
  buckets: { deletions: Map<number, MoveEntry[]>; insertions: Map<number, MoveEntry[]> },
): void {
  const consumed = new Set<MoveEntry>();
  for (const [hash, delEntries] of buckets.deletions) {
    const insEntries = buckets.insertions.get(hash);
    if (!insEntries) continue;
    for (const del of delEntries) {
      const ins = insEntries.find(
        (e) =>
          !consumed.has(e) &&
          e.slideIdx !== del.slideIdx &&
          e.words.join(" ").toLowerCase() === del.words.join(" ").toLowerCase(),
      );
      if (!ins) continue;
      consumed.add(ins);
      const dc = slideResults[del.slideIdx]!;
      const ic = slideResults[ins.slideIdx]!;
      const from = dc.origSlideIndex ?? dc.modSlideIndex ?? del.slideIdx;
      const to = ic.modSlideIndex ?? ic.origSlideIndex ?? ins.slideIdx;
      _markMoved(slideResults, del, from, to);
      _markMoved(slideResults, ins, from, to);
    }
  }
}

export function detectMoves(slideResults: SlideComparison[]): SlideComparison[] {
  if (!slideResults || slideResults.length < 2) return slideResults || [];
  _matchMoves(slideResults, _indexHunks(slideResults));
  return slideResults;
}

// ---------------------------------------------------------------------------
// 5. generateComparisonReport
// ---------------------------------------------------------------------------

/**
 * High-level orchestrator: matches slides, diffs shapes, detects moves,
 * and returns a complete comparison report.
 */
export function generateComparisonReport(originalSlides: ExtractedSlide[], modifiedSlides: ExtractedSlide[]): ComparisonReport {
  const origArr = originalSlides || [];
  const modArr = modifiedSlides || [];

  // Step 1: match slides
  const { matched, unmatchedOrig, unmatchedMod } = matchSlides(origArr, modArr);

  // Build lookup maps for quick access
  const origByIndex = new Map<number, ExtractedSlide>(origArr.map((s) => [s.slideIndex, s]));
  const modByIndex = new Map<number, ExtractedSlide>(modArr.map((s) => [s.slideIndex, s]));

  const slideComparisons: SlideComparison[] = [];
  let unchangedCount = 0;
  let modifiedCount = 0;

  // Step 2: process matched pairs
  for (const m of matched) {
    const origSlide = origByIndex.get(m.origIdx)!;
    const modSlide = modByIndex.get(m.modIdx)!;
    const shapeResults = diffSlideShapes(
      origSlide.shapes || [],
      modSlide.shapes || []
    );

    const isUnchanged = shapeResults.every((r) => r.status === "unchanged");
    const status: SlideStatus = isUnchanged ? "unchanged" : "modified";
    if (isUnchanged) unchangedCount++;
    else modifiedCount++;

    slideComparisons.push({
      origSlideIndex: m.origIdx,
      modSlideIndex: m.modIdx,
      origSlideNumber: origSlide.slideNumber,
      modSlideNumber: modSlide.slideNumber,
      similarity: m.similarity,
      status,
      shapeResults,
    });
  }

  // Step 3: add removed slides (only in original)
  for (const idx of unmatchedOrig) {
    const origSlide = origByIndex.get(idx)!;
    slideComparisons.push({
      origSlideIndex: idx,
      modSlideIndex: null,
      origSlideNumber: origSlide.slideNumber,
      modSlideNumber: null,
      similarity: 0,
      status: "removed",
      shapeResults: (origSlide.shapes || []).map((s) => ({
        origShape: s,
        modShape: null,
        status: "deleted" as const,
        diffs: s.text ? [{ type: "delete" as const, text: s.text }] : [],
      })),
    });
  }

  // Step 4: add new slides (only in modified)
  for (const idx of unmatchedMod) {
    const modSlide = modByIndex.get(idx)!;
    slideComparisons.push({
      origSlideIndex: null,
      modSlideIndex: idx,
      origSlideNumber: null,
      modSlideNumber: modSlide.slideNumber,
      similarity: 0,
      status: "added",
      shapeResults: (modSlide.shapes || []).map((s) => ({
        origShape: null,
        modShape: s,
        status: "added" as const,
        diffs: s.text ? [{ type: "insert" as const, text: s.text }] : [],
      })),
    });
  }

  // Step 5: detect moves across slides
  detectMoves(slideComparisons);

  // Step 6: sort comparisons by slide order (orig first, then mod)
  slideComparisons.sort((a, b) => {
    const aKey = a.origSlideIndex ?? a.modSlideIndex ?? 0;
    const bKey = b.origSlideIndex ?? b.modSlideIndex ?? 0;
    return aKey - bKey;
  });

  // Build summary
  const summary: ComparisonSummary = {
    totalOrig: origArr.length,
    totalMod: modArr.length,
    matched: matched.length,
    added: unmatchedMod.length,
    removed: unmatchedOrig.length,
    unchanged: unchangedCount,
    modified: modifiedCount,
  };

  return { summary, slideComparisons };
}
